import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { listInserate } from "@/api/inserate";
import { InserateFilters } from "@/components/inserate/InserateFilters";
import { InserateTable } from "@/components/inserate/InserateTable";
import {
  EMPTY_FILTERS,
  applyClientFilters,
  toApiParams,
  type InserateFilters as Filters,
} from "@/components/inserate/filterModel";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export function InseratePage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  const apiParams = useMemo(() => toApiParams(filters), [filters]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["inserate", apiParams],
    queryFn: () => listInserate(apiParams),
    refetchInterval: 60_000,
    refetchOnMount: true,
  });

  const visible = useMemo(
    () => (data ? applyClientFilters(data.inserate, filters) : []),
    [data, filters],
  );

  return (
    <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Inserate</h1>
        <p className="text-sm text-muted-foreground">
          Triage-Queue für neue Anlageobjekte.
          {data && (
            <span className="ml-2" data-testid="inserate-count">
              · {visible.length} von {data.total}{" "}
              {data.total === 1 ? "Inserat" : "Inserate"}
              {isFetching ? " · aktualisiert …" : ""}
            </span>
          )}
        </p>
      </header>

      <InserateFilters value={filters} onChange={setFilters} />

      {isLoading && <InserateLoading />}

      {isError && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-6"
          data-testid="inserate-error"
        >
          <p className="text-sm font-medium text-destructive">
            Inserate konnten nicht geladen werden.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {error instanceof Error ? error.message : "Unbekannter Fehler."}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void refetch()}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {data && visible.length === 0 && (
        <div
          className="rounded-lg border p-10 text-center"
          data-testid="inserate-empty"
        >
          <p className="text-sm font-medium">
            {data.total === 0
              ? "Keine Inserate."
              : "Keine Treffer für die aktuellen Filter."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {data.total === 0
              ? "Lass den poll_scheduler laufen oder lege manuell ein Inserat an."
              : "Filter zurücksetzen, um alle Inserate zu sehen."}
          </p>
        </div>
      )}

      {data && visible.length > 0 && <InserateTable data={visible} />}
    </main>
  );
}

function InserateLoading() {
  return (
    <div
      className="space-y-2 rounded-lg border p-3"
      data-testid="inserate-loading"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
